<template>
  <div
    ref="strip"
    class="tabs"
    :class="{ 'tabs--more-start': moreStart, 'tabs--more-end': moreEnd }"
    role="tablist"
    @keydown="onKey"
    @scroll.passive="measure"
  >
    <button
      v-for="(entry, index) in tabs" :key="entry.id"
      :ref="element => { buttons[index] = element }"
      type="button"
      role="tab"
      class="tabs__tab"
      :class="{ 'tabs__tab--on': entry.id === modelValue }"
      :aria-selected="entry.id === modelValue"
      :tabindex="entry.id === modelValue ? 0 : -1"
      @click="$emit('update:modelValue', entry.id)"
    >
      {{ entry.label }}
      <span v-if="entry.count" class="tabs__count">{{ entry.count }}</span>
    </button>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

/**
 * One row of tabs over one subject.
 *
 * The three screens that had grown their own copy of this markup are what put it
 * here (a component copied between modules is a bug, says this package's own
 * header). What the copies did not have is the keyboard: a tablist is arrow
 * keys, and rebuilding that per screen is how one of them ends up without it.
 *
 * A tab that a permission does not allow is not disabled here — the caller
 * leaves it out of the list. A greyed tab still announces that the thing behind
 * it exists.
 */
const props = defineProps({
  // [{ id, label, count? }] — count is drawn only when it is a real number.
  tabs: { type: Array, required: true },
  modelValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const buttons = ref([])
const strip = ref(null)

// ── Que se note que hay más, y que la elegida se vea ─────────────────────────
//
// En un teléfono la tira no tiene barra (con el dedo no hace falta), así que
// nada decía que detrás del borde quedaban seis pestañas más: la ficha de una
// obra tiene quince y mide casi 2.000 px. Se difumina el borde por el que queda
// algo, que es como lo dice cualquier aplicación del teléfono.
//
// Y la pestaña elegida se trae a la vista —al centro de la tira, sin mover la
// página—: elegir la séptima y verla desaparecer por la derecha, o volver a una
// ficha y encontrar su pestaña fuera de la pantalla, es lo que delata una web.
const moreStart = ref(false)
const moreEnd = ref(false)

function measure() {
  const element = strip.value
  if (!element) return
  moreStart.value = element.scrollLeft > 2
  moreEnd.value = element.scrollLeft + element.clientWidth < element.scrollWidth - 2
}

function reveal(smooth) {
  const element = strip.value
  const index = props.tabs.findIndex(entry => entry.id === props.modelValue)
  const button = buttons.value[index]
  if (!element || !button || element.scrollWidth <= element.clientWidth) return measure()
  const left = button.offsetLeft - (element.clientWidth - button.offsetWidth) / 2
  element.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' })
  measure()
}

let observer = null
onMounted(() => {
  nextTick(() => reveal(false))
  if (typeof ResizeObserver !== 'undefined' && strip.value) {
    observer = new ResizeObserver(() => measure())
    observer.observe(strip.value)
  }
})
onBeforeUnmount(() => observer?.disconnect())

watch(() => props.modelValue, () => nextTick(() => reveal(true)))
watch(() => props.tabs.length, () => nextTick(() => reveal(false)))

// Tabs come and go while a screen is open: a module's panel arrives late, a
// filter empties one. Landing on a tab that no longer exists would show an empty
// page with nothing selected, so the first one takes over.
watch(() => props.tabs, (tabs) => {
  if (!tabs.length) return
  if (!tabs.some(entry => entry.id === props.modelValue)) emit('update:modelValue', tabs[0].id)
}, { immediate: true, deep: true })

function onKey(event) {
  const keys = { ArrowRight: 1, ArrowLeft: -1 }
  const step = keys[event.key]
  if (!step && event.key !== 'Home' && event.key !== 'End') return

  event.preventDefault()
  const current = props.tabs.findIndex(entry => entry.id === props.modelValue)
  const last = props.tabs.length - 1
  const next = event.key === 'Home' ? 0
    : event.key === 'End' ? last
      : (current + step + props.tabs.length) % props.tabs.length

  emit('update:modelValue', props.tabs[next].id)
  buttons.value[next]?.focus()
}
</script>

<style scoped>
.tabs {
  display: flex;
  gap: var(--space-1);
  overflow-x: auto;
  scrollbar-width: none;

  /* ── El eje vertical, quieto ──────────────────────────────────────────────

     `overflow-x: auto` NO deja el otro eje en paz: el navegador convierte el
     `visible` de `overflow-y` en `auto`. Y a la tira le sobraba justo 1 px de
     alto —el que la pestaña activa se salía con su `margin-bottom: -1px` para
     pisar la línea de abajo—, así que salía una segunda barra, vertical, para
     deslizar un píxel. Medido en la ficha de obra: 44 px de contenido en 43 de
     hueco, a 1280 y a 900.

     La línea pasa a ser sombra interior en vez de borde. Así vive dentro de la
     caja: la pestaña activa la pisa con su subrayado sin tener que salirse, no
     sobra ningún píxel, y el eje vertical se puede clavar. */
  overflow-y: hidden;
  box-shadow: inset 0 -1px 0 var(--color-border);
}

/* ── Que se note que hay más ─────────────────────────────────────────────────

   La tira desliza desde siempre, pero con la barra escondida nadie lo sabía. El
   archivador de una ficha de cliente tiene DOCE pestañas y mide 1.274 px: la
   última queda cortada incluso en un monitor de 1512, y quien la mira ve una
   palabra a medias y ninguna manera de llegar a ella. Con el dedo el gesto es
   evidente; con un ratón no hay gesto, hay una barra — y no estaba.

   Cuatro píxeles y del color del borde: se ve si se busca y no compite con
   nada. `pointer: fine` y no un ancho de pantalla, porque lo que decide es con
   qué se señala. */
@media (pointer: fine) {
  .tabs { scrollbar-width: thin; scrollbar-color: var(--color-border) transparent; }
  .tabs::-webkit-scrollbar { height: 4px; }
  .tabs::-webkit-scrollbar-track { background: transparent; }
  .tabs::-webkit-scrollbar-thumb { background: var(--color-border); border-radius: 2px; }
}
.tabs::-webkit-scrollbar { display: none; }

/* El borde por el que queda algo, difuminado. Una máscara y no una sombra
   encima: no tapa el color de nada ni recibe toques. */
.tabs--more-end { -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 32px), transparent); mask-image: linear-gradient(to right, #000 calc(100% - 32px), transparent); }
.tabs--more-start { -webkit-mask-image: linear-gradient(to left, #000 calc(100% - 32px), transparent); mask-image: linear-gradient(to left, #000 calc(100% - 32px), transparent); }
.tabs--more-start.tabs--more-end {
  -webkit-mask-image: linear-gradient(to right, transparent, #000 32px, #000 calc(100% - 32px), transparent);
  mask-image: linear-gradient(to right, transparent, #000 32px, #000 calc(100% - 32px), transparent);
}

.tabs__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex: 0 0 auto;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: var(--space-2) var(--space-3) var(--space-3);
  font: inherit;
  font-size: var(--text-md);
  color: var(--color-text-soft);
  cursor: pointer;
  transition: color var(--fast), border-color var(--fast);
}
.tabs__tab:hover { color: var(--color-text); }
.tabs__tab--on {
  color: var(--color-text);
  border-bottom-color: var(--color-primary);
  font-weight: 600;
}

.tabs__count {
  font-size: var(--text-xs);
  color: var(--color-text-soft);
  background: var(--color-surface-2);
  border-radius: var(--radius-full);
  padding: 0 var(--space-2);
  line-height: 1.5;
}
</style>
